import React from 'react';
import {
  Button,
  colors,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeading,
  TableRow,
} from '@insurancemenu/imenu-components-lib';
import clsx from 'clsx';
import { useRootStore } from 'store/rootStore';
import { observer } from 'mobx-react-lite';
import SubscriberStatus from 'components/common/election/SubscriberStatus';
import { NavigationClose } from '@insurancemenu/imenu-components-lib/dist/icons';
import { createStyles as createModalStyles } from 'components/common/modals/ActionModal/styles';
import { EmployeeInvitationStatusTypes, SubscriberSERoles } from 'types/electionService';
import { createStyles } from './styles';

interface InviteModalProps {
  show: boolean;
  onClose: () => void;
  onAction: () => void;
}

// if employee isn't invited or has the 'EMPLOYER' role, it's an opportunity to remove him from modal,
const isInvitationEditable = (status: EmployeeInvitationStatusTypes, subSERole: SubscriberSERoles): boolean => {
  return (
    status === EmployeeInvitationStatusTypes.NOT_YET_INVITED ||
    status === EmployeeInvitationStatusTypes.ADD_MAIL ||
    subSERole === SubscriberSERoles.EMPLOYER
  );
};

const InviteModal = observer(({ show, onClose, onAction }: InviteModalProps): JSX.Element => {
  const classes = createStyles();
  const modalStyles = createModalStyles(true); // common modal styles

  const {
    inviteModalStore: {
      loading,
      subscribers,
      showRemindInvitationBtn,
      areAllSubscribersWithoutEmail,
      isInviteAllBtnDisabled,
      unselectSubscriber,
      inviteSingleUser,
      remindRegUsersAboutInvitation,
      remindSingleRegUserAboutInvitation,
    },
  } = useRootStore();

  const resendInvite = (subscriberId: string): void => {
    void inviteSingleUser(subscriberId);
  };
  const sendInvite = (subscriberId: string): void => {
    void inviteSingleUser(subscriberId);
  };
  const remindAboutInvitation = (subscriberId: string): void => {
    void remindSingleRegUserAboutInvitation(subscriberId);
  };

  const actionButton = (text: string, action: () => void): JSX.Element => {
    return (
      <Button type="text" size="medium" color="primary" onClick={action} className={classes.textBtn}>
        {text}
      </Button>
    );
  };

  const inviteActionButton = (status: EmployeeInvitationStatusTypes, subscriberId: string): JSX.Element => {
    switch (status) {
      case EmployeeInvitationStatusTypes.INVITED:
        return actionButton('Resend Invite', (): void => resendInvite(subscriberId));
      case EmployeeInvitationStatusTypes.NOT_YET_INVITED:
        return actionButton('Send Invite', (): void => sendInvite(subscriberId));
      case EmployeeInvitationStatusTypes.REGISTERED:
        return actionButton('REMIND', (): void => remindAboutInvitation(subscriberId));
      default:
        return <></>;
    }
  };

  const employeeRows = subscribers.map(
    ({ id, common: { firstName, lastName }, email, selfEnrollStatus, subSERole }) => (
      <TableRow className={classes.tableRow} key={id}>
        <TableCell className={classes.tableCell}>{`${firstName} ${lastName}`}</TableCell>
        <TableCell className={clsx(classes.tableCell, classes.email)}>{email}</TableCell>
        {/* Status */}
        <TableCell className={classes.tableCell}>
          <SubscriberStatus status={selfEnrollStatus as EmployeeInvitationStatusTypes} subSERole={subSERole} />
        </TableCell>
        <TableCell className={classes.tableCell}>
          {subSERole === SubscriberSERoles.EMPLOYER ? (
            <></>
          ) : (
            inviteActionButton(selfEnrollStatus as EmployeeInvitationStatusTypes, id)
          )}
        </TableCell>
        <TableCell className={classes.tableCell}>
          {isInvitationEditable(selfEnrollStatus as EmployeeInvitationStatusTypes, subSERole) && (
            <Button
              type="icon"
              color="custom"
              customColor={colors.red[600]}
              onClick={(): void => {
                unselectSubscriber(id);
              }}
            >
              <NavigationClose width="3rem" />
            </Button>
          )}
        </TableCell>
      </TableRow>
    ),
  );

  return (
    <Modal show={show} onClose={onClose} className={clsx(modalStyles.modal, classes.inviteModal)}>
      <h3 className={modalStyles.modalTitle}>Batch Invite Employees</h3>
      <p className={clsx(modalStyles.contentText, classes.modalSubtitle)}>
        Enter an email address for each employee that you want to invite to self-enroll. The employee will receive an
        email with a link to create their account and make elections.
      </p>

      <footer className={classes.modalFooter}>
        <div className={classes.tableWrapper}>
          <Table loading={loading} totalElements={subscribers.length} maxHeight="40rem" minHeight="40rem">
            <TableHeader className={classes.tableHeader}>
              <TableRow>
                <TableHeading className={clsx(classes.tableHeading)}>Employee</TableHeading>
                <TableHeading className={clsx(classes.tableHeading, classes.email)}>Email Address</TableHeading>
                <TableHeading className={clsx(classes.tableHeading)}>Status</TableHeading>
                <TableHeading className={clsx(classes.tableHeading)} />
                <TableHeading className={clsx(classes.tableHeading)} />
              </TableRow>
            </TableHeader>
            <TableBody className={classes.tableBody}>{employeeRows}</TableBody>
          </Table>
        </div>
        <p className={classes.guidelineMessage}>
          {`Click the ${
            showRemindInvitationBtn ? 'buttons' : 'button'
          } below to send invitation emails to employees who have not yet been invited or remind about
          invitation.`}
        </p>
        <div className={classes.actionsBtnWrapper}>
          <Button color="primary" onClick={onAction} size="large" disabled={isInviteAllBtnDisabled}>
            Invite all Users
          </Button>
          {showRemindInvitationBtn && (
            <Button
              color="primary"
              type="outlined"
              onClick={remindRegUsersAboutInvitation}
              size="large"
              disabled={areAllSubscribersWithoutEmail}
            >
              Remind all Users
            </Button>
          )}
        </div>
      </footer>
    </Modal>
  );
});

export default InviteModal;
